import { supabase } from "@/integrations/supabase/client";
import { Property } from "@/types/property";
import { handleAsyncError } from "@/utils/errorUtils";
import mapSupabaseProperty from "@/utils/mappers/propertyMapper";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "react-router-dom";

// Utility function to sort properties by sortOrder (ascending, nulls last)
const sortPropertiesBySortOrder = (properties: Property[]): Property[] => {
  return [...properties].sort((a, b) => {
    const sortA = a.sortOrder ?? 999;
    const sortB = b.sortOrder ?? 999;
    return sortA - sortB;
  });
};

export const useProperties = () => {
  const location = useLocation();
  const isAdmin = location?.pathname?.startsWith("/admin");

  const { profile } = useAuth();
  const role = profile?.role;
  const userId = profile?.user_id;

  const skipRoleFilter = !isAdmin;

  return useQuery({
    queryKey: ["properties", role, userId, skipRoleFilter],
    queryFn: async (): Promise<Property[]> => {
      const result = await handleAsyncError(async () => {
        let query;

        if (skipRoleFilter || role === "super_admin") {
          query = supabase
            .from("properties")
            .select(
              `
              *,
              hosts (*),
              neighborhoods (*),
              property_highlights (*),
              reviews (*)
            `,
            )
            .order("sort_order", { ascending: true, nullsFirst: false });
        } else if (userId) {
          query = supabase
            .from("user_property_assignments")
            .select(
              `
              *,
              properties (
                *,
                hosts (*),
                neighborhoods (*),
                property_highlights (*),
                reviews (*)
              )
            `,
            )
            .eq("user_id", userId);
        } else {
          return [];
        }

        const { data, error } = await query;

        if (error) {
          console.error("Supabase error:", error);
          throw error;
        }

        if (skipRoleFilter || role === "super_admin") {
          return sortPropertiesBySortOrder((data ?? []).map(mapSupabaseProperty));
        }

        return sortPropertiesBySortOrder(
          data?.map((assignment) =>
            mapSupabaseProperty(assignment.properties),
          ) ?? []
        );
      }, "Properties fetch");

      return result ?? [];
    },
    enabled: skipRoleFilter || !!role,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useProperty = (propertyId: string) => {
  return useQuery({
    queryKey: ["property", propertyId],
    queryFn: async (): Promise<Property | null> => {
      const result = await handleAsyncError(async () => {
        const { data: property, error } = await supabase
          .from("properties")
          .select(
            `
            *,
            hosts (*),
            neighborhoods (*),
            property_highlights (*),
            reviews (*)
          `,
          )
          .eq("id", propertyId)
          .single();

        if (error) {
          throw error;
        }

        return property;
      }, `Property ${propertyId} fetch`);

      if (!result) {
        return null;
      }

      const mappedProperty = mapSupabaseProperty(result);

      return mappedProperty;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};